import { Navigate } from 'react-router-dom';

import { Spinner } from '@/components/atoms/Spinner';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth } from '@/contexts/auth';
import { useCage } from '@/contexts/cage';
import { useGetCageWinch } from '@/domain/cageWinch';
import { isBioscopeV2 } from '@/utils/bioscopeVersionHelpers';

import type { CagePageInterface } from '../../utils/cage-pages-interface';

import CageWinchControlPageContent from './CageWinchControlPageContent';
import { EMPTY_WINCH_USER } from './winchMqttApi';

function CageWinchControlContainer({ clientId, locationId, cageId }: CagePageInterface) {
    const { user } = useAuth();
    const { loading: cageLoading, bioscope } = useCage();
    const winchQuery = useGetCageWinch(cageId, {
        staleTime: 60 * 1000,
    });
    const { isLoading, error, data: winchData } = winchQuery;

    if (cageLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    if (!isBioscopeV2(bioscope?.version)) {
        return (
            <Navigate
                to={`/c/${clientId}/location/${locationId}/pen/${cageId}/dashboard`}
                replace
            />
        );
    }

    if (isLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    // The page's title goes with the content, so the error state stands in as its h1.
    if (error) {
        return (
            <SomethingHappened
                error={error}
                headingLevel="h1"
                {...retryFailedQueries(winchQuery)}
            />
        );
    }

    const mqttUser = user
        ? {
              id: user.id,
              name: `${user.firstName} ${user.lastName}`.trim() || user.email,
              email: user.email,
              role: user.systemRole,
          }
        : EMPTY_WINCH_USER;

    return (
        <CageWinchControlPageContent
            key={winchData?.winchId ?? `cage-${cageId}`}
            winchData={winchData}
            user={mqttUser}
        />
    );
}

export default CageWinchControlContainer;
export { CageWinchControlContainer };
